import { useState } from 'react';
import { Alert, Button, Group, Stack, Text, Textarea } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useTaskRepository, useTaskStatusRepository } from '@ayocore/tasks/client/rpc';
import { commentRepository } from '@ayocore/comments/client/rpc';
import logger from '@ayocore/platform/common/helpers/logger';
import type { Task } from '@ayocore/tasks/schemas/task.schema';

/**
 * Модалка завершения задачи: обязательный комментарий + перевод задачи
 * в финальный статус (slug `FINISHED` для её task_type).
 *
 * Логика повторяет `ActivityTaskViewCommandForm.handleConfirmComplete`:
 * - найти `FINISHED` статус через `taskStatusRepository.findByTaskType`
 * - если у задачи есть `entity_type + entity_uuid` — создать комментарий
 *   к связанной CRM-сущности (он попадёт в Activity лида/партнёра/сделки)
 * - обновить `task_status_id` задачи
 */
export function CompleteTaskModal({
  task,
  onCompleted,
  onCancel,
}: {
  task: Task;
  /** Колбэк после успешного завершения задачи. */
  onCompleted: () => void;
  /** Закрыть без действий (отмена). */
  onCancel: () => void;
}) {
  const taskRepository = useTaskRepository();
  const taskStatusRepository = useTaskStatusRepository();

  const [commentText, setCommentText] = useState('');
  const [completing, setCompleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!task.task_type_id) {
      setErrorMessage('У задачи не указан тип — невозможно подобрать финальный статус.');
      return;
    }
    const trimmed = commentText.trim();
    if (!trimmed) return;

    setCompleting(true);
    setErrorMessage(null);
    try {
      const statuses = await taskStatusRepository.findByTaskType(task.task_type_id);
      const finishedStatus = statuses.find(status => status.slug === 'FINISHED');
      if (!finishedStatus) {
        setErrorMessage('Не удалось найти финальный статус для типа задачи.');
        return;
      }

      if (task.entity_type && task.entity_uuid) {
        await commentRepository.create({
          entity_uuid: task.entity_uuid,
          entity_type: task.entity_type,
          content: `Комментарий к задаче "#${task.id} ${task.title}": ${trimmed}`,
        });
      }

      await taskRepository.update(task.id, { task_status_id: finishedStatus.id });
      onCompleted();
    } catch (error) {
      logger.error('[CompleteTaskModal] Failed to complete task', {
        taskId: task.id,
        error,
      });
      setErrorMessage('Не удалось завершить задачу.');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <Stack gap='md'>
      <Text size='sm' c='dimmed'>
        Опишите результат — комментарий будет добавлен к связанной сущности.
      </Text>
      {errorMessage && (
        <Alert color='red' p='xs' title='Ошибка' withCloseButton onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}
      <Textarea
        value={commentText}
        onChange={event => setCommentText(event.currentTarget.value)}
        placeholder='Комментарий о завершении...'
        minRows={4}
        autosize
        disabled={completing}
      />
      <Group justify='flex-end'>
        <Button variant='outline' color='gray' onClick={onCancel} disabled={completing}>
          Отмена
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          loading={completing}
          disabled={!commentText.trim()}
          leftSection={<IconCheck size={14} />}
          color='green'
        >
          Завершить
        </Button>
      </Group>
    </Stack>
  );
}
